// helixAuth.js – unified onboarding (performance optimized)
import { showAlert } from './alerts.js'; // kept if needed elsewhere

(async function () {
  'use strict';

  // ============================================================
  // 🔥 Cache all DOM references once
  // ============================================================
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [
    ...context.querySelectorAll(selector),
  ];

  const modal = document.getElementById('unified-auth-modal');
  const trigger = document.getElementById('unified-auth-trigger');
  const closeButtons = $$('#unified-auth-modal .js-modal-close');

  const statePhone = document.getElementById('ua-state-phone');
  const stateStorePwd = document.getElementById('ua-state-store-password');
  const stateAdminPwd = document.getElementById('ua-state-admin-password');
  const stateUsername = document.getElementById('ua-state-username');
  const stateSuccess = document.getElementById('ua-state-success');
  const stateError = document.getElementById('ua-state-error');

  const phoneForm = document.getElementById('ua-phone-form');
  const phoneInput = document.getElementById('ua-phone');
  const storePwdForm = document.getElementById('ua-store-password-form');
  const storePwdInput = document.getElementById('ua-store-password');
  const adminPwdForm = document.getElementById('ua-admin-password-form');
  const adminPwdInput = document.getElementById('ua-admin-password');
  const usernameForm = document.getElementById('ua-username-form');
  const usernameInput = document.getElementById('ua-username');
  const usernameStatus = document.getElementById('ua-username-status');
  const skipBtn = document.getElementById('ua-skip-username');
  const successMsg = document.getElementById('ua-success-message');
  const errorMsg = document.getElementById('ua-error-message');
  const retryBtn = document.getElementById('ua-retry');

  // Suggestion container (cached)
  const suggestionContainer = $('#ua-state-username .flex.flex-wrap.gap-2');

  // Button state helper
  function setButtonLoading(btn, isLoading, loadingText, normalHTML) {
    if (!btn) return;
    btn.disabled = isLoading;
    btn.innerHTML = isLoading ? loadingText : normalHTML;
  }

  // ============================================================
  // 🔥 State
  // ============================================================
  let currentPhone = '';
  let detectedRole = '';

  // ============================================================
  // 🔥 Modal open/close
  // ============================================================
  function openModal() {
    resetToStep('phone');
    phoneInput?.focus();
  }

  function closeModal() {
    // External modal logic (e.g., adding/removing hidden) is handled elsewhere
    // We only reset if needed
  }

  trigger?.addEventListener('click', openModal);
  closeButtons.forEach(btn => btn.addEventListener('click', closeModal));
  // backdrop click handler – if present
  const backdrop = modal?.querySelector('.js-modal-close.bg-black\\/60');
  backdrop?.addEventListener('click', closeModal);

  // ============================================================
  // 🔥 Step management
  // ============================================================
  const stepElements = [
    statePhone,
    stateStorePwd,
    stateAdminPwd,
    stateUsername,
    stateSuccess,
    stateError,
  ];

  function showOnly(stepElement) {
    stepElements.forEach(el => el?.classList.add('hidden'));
    stepElement?.classList.remove('hidden');
  }

  async function resetToStep(step) {
    switch (step) {
      case 'phone':
        showOnly(statePhone);
        if (phoneInput) phoneInput.value = '';
        break;
      case 'store-password':
        showOnly(stateStorePwd);
        if (storePwdInput) storePwdInput.value = '';
        break;
      case 'admin-password':
        showOnly(stateAdminPwd);
        if (adminPwdInput) adminPwdInput.value = '';
        break;
      case 'username':
        showOnly(stateUsername);
        if (usernameInput) usernameInput.value = '';
        if (usernameStatus) usernameStatus.textContent = '';
        // Refresh suggestions
        await refreshSuggestions();
        break;
      case 'success':
        showOnly(stateSuccess);
        break;
      case 'error':
        showOnly(stateError);
        break;
      default:
        showOnly(statePhone);
    }
  }

  // ============================================================
  // 🔥 API calls (cached axios instances for cancellation)
  // ============================================================
  async function checkPhone(phone) {
    try {
      const res = await axios.post('/api/v1/users/check-phone', { phone });
      return res.data; // { exists, role }
    } catch (err) {
      throw err.response?.data?.message || 'Erreur réseau';
    }
  }

  async function storeLogin(phone, password) {
    const res = await axios.post('/api/v1/users/login', { phone, password });
    return res.data;
  }

  async function customerLogin(phone) {
    const res = await axios.post('/api/v1/users/login', { phone });
    return res.data;
  }

  async function createUser(phone, username = null) {
    const res = await axios.post('/api/v1/users/create-user', {
      phone,
      username,
    });
    return res.data;
  }

  // ============================================================
  // 🔥 Suggestion generator (optimized: deduplicated words, no batch check)
  // ============================================================
  const SUGGESTION_WORDS = [
    'deal',
    'shop',
    'market',
    'member',
    'buyer',
    'seller',
    'fan',
    'expert',
    'shopper',
    'dealer',
    'user',
    'visitor',
    'client',
    'pro',
    'hunter',
    'finder',
    'star',
    'master',
    'boss',
    'champ',
    'hero',
    'geek',
    'merchant',
    'store',
    'offer',
    'flash',
    'venture',
  ];

  function generateSuggestions(phone) {
    const lastFour = phone.replace(/\D/g, '').slice(-4) || '0000';
    const used = new Set();
    const suggestions = [];
    const pool = [...SUGGESTION_WORDS];

    while (suggestions.length < 3 && pool.length > 0) {
      const randomIndex = Math.floor(Math.random() * pool.length);
      const word = pool.splice(randomIndex, 1)[0];
      const candidate = `${word}_${lastFour}`;
      if (!used.has(candidate)) {
        used.add(candidate);
        suggestions.push(candidate);
      }
    }

    return suggestions.length
      ? suggestions
      : [`member_${lastFour}`, `helix_${lastFour}`, `user_${lastFour}`];
  }

  async function refreshSuggestions() {
    if (!suggestionContainer) return;
    const suggestions = generateSuggestions(
      currentPhone || phoneInput?.value || ''
    );
    suggestionContainer.innerHTML = suggestions
      .map(
        s =>
          `<button class="suggestion-chip text-xs bg-gray-100 px-3 py-1 rounded-full" type="button" data-suggestion="${s}">${s}</button>`
      )
      .join('');
  }

  // ============================================================
  // 🔥 Username availability check (debounced)
  // ============================================================
  let usernameCheckTimer = null;
  async function checkUsernameAvailability(username) {
    if (!username || username.length < 3) {
      usernameStatus.innerHTML = '';
      return;
    }
    try {
      const res = await axios.get(`/api/v1/users/check-username/${username}`);
      if (res.data.available) {
        usernameStatus.innerHTML =
          '<span class="text-green-500"><i class="fas fa-check-circle"></i></span>';
      } else {
        usernameStatus.innerHTML =
          '<span class="text-red-500"><i class="fas fa-times-circle"></i></span>';
      }
    } catch (err) {
      usernameStatus.innerHTML = '';
    }
  }

  function debounceUsernameCheck(value) {
    clearTimeout(usernameCheckTimer);
    usernameCheckTimer = setTimeout(
      () => checkUsernameAvailability(value),
      300
    );
  }

  // Suggestion chip click (event delegation)
  document.addEventListener('click', e => {
    const chip = e.target.closest('.suggestion-chip');
    if (!chip) return;
    const suggestion = chip.dataset.suggestion;
    if (suggestion) {
      usernameInput.value = suggestion;
      debounceUsernameCheck(suggestion);
    }
  });

  usernameInput?.addEventListener('input', e =>
    debounceUsernameCheck(e.target.value.trim())
  );

  // ============================================================
  // 🔥 Form submit handlers (with loading state helper)
  // ============================================================
  phoneForm?.addEventListener('submit', async e => {
    e.preventDefault();
    const phone = phoneInput?.value.trim().replace(/\s/g, '');
    if (!phone) return;

    const submitBtn = phoneForm.querySelector('button[type="submit"]');
    setButtonLoading(
      submitBtn,
      true,
      '<i class="fas fa-spinner fa-spin"></i> Vérification...',
      '<i class="fas fa-arrow-right mr-2"></i>Continuer'
    );

    currentPhone = phone;

    try {
      const { exists, role } = await checkPhone(phone);
      detectedRole = role;

      if (exists && role === 'customer') {
        await customerLogin(phone);
        successMsg.textContent = 'Vous êtes connecté !';
        resetToStep('success');
        setTimeout(() => {
          closeModal();
          location.reload();
        }, 1500);
      } else if (exists && role === 'store') {
        resetToStep('store-password');
      } else if (exists && role === 'admin') {
        resetToStep('admin-password');
      } else {
        resetToStep('username');
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Numéro invalide';
      showError(message);
    } finally {
      setButtonLoading(
        submitBtn,
        false,
        '',
        '<i class="fas fa-arrow-right mr-2"></i>Continuer'
      );
    }
  });

  storePwdForm?.addEventListener('submit', async e => {
    e.preventDefault();
    const password = storePwdInput?.value;
    if (!password) return;

    const submitBtn = storePwdForm.querySelector('button[type="submit"]');
    setButtonLoading(
      submitBtn,
      true,
      '<i class="fas fa-spinner fa-spin"></i> Connexion...',
      '<i class="fas fa-lock mr-2"></i>Se connecter'
    );

    try {
      await storeLogin(currentPhone, password);
      successMsg.textContent = 'Boutique connectée avec succès !';
      resetToStep('success');
      setTimeout(() => {
        closeModal();
        location.reload();
      }, 1500);
    } catch (err) {
      showError(err.response?.data?.message || 'Mot de passe incorrect');
    } finally {
      setButtonLoading(
        submitBtn,
        false,
        '',
        '<i class="fas fa-lock mr-2"></i>Se connecter'
      );
    }
  });

  adminPwdForm?.addEventListener('submit', async e => {
    e.preventDefault();
    const password = adminPwdInput?.value;
    if (!password) return;

    const submitBtn = adminPwdForm.querySelector('button[type="submit"]');
    setButtonLoading(
      submitBtn,
      true,
      '<i class="fas fa-spinner fa-spin"></i> Connexion...',
      '<i class="fas fa-lock mr-2"></i>Se connecter'
    );

    try {
      await storeLogin(currentPhone, password); // reuse same login for admin
      successMsg.textContent = 'Admin connecté avec succès !';
      resetToStep('success');
      setTimeout(() => {
        closeModal();
        location.reload();
      }, 1500);
    } catch (err) {
      showError(err.response?.data?.message || 'Mot de passe incorrect');
    } finally {
      setButtonLoading(
        submitBtn,
        false,
        '',
        '<i class="fas fa-lock mr-2"></i>Se connecter'
      );
    }
  });

  usernameForm?.addEventListener('submit', async e => {
    e.preventDefault();
    const usernameValue = usernameInput?.value.trim() || null;
    const createBtn = document.getElementById('ua-create-account');
    setButtonLoading(
      createBtn,
      true,
      '<i class="fas fa-spinner fa-spin"></i> Création...',
      '<i class="fas fa-user-check mr-2"></i>Créer mon compte'
    );

    try {
      await createUser(currentPhone, usernameValue);
      successMsg.textContent = 'Votre compte est prêt !';
      resetToStep('success');
      setTimeout(() => {
        closeModal();
        location.reload();
      }, 1500);
    } catch (err) {
      showError(err.response?.data?.message || 'Erreur de création');
    } finally {
      setButtonLoading(
        createBtn,
        false,
        '',
        '<i class="fas fa-user-check mr-2"></i>Créer mon compte'
      );
    }
  });

  skipBtn?.addEventListener('click', async () => {
    const createBtn = document.getElementById('ua-create-account');
    setButtonLoading(
      createBtn,
      true,
      '<i class="fas fa-spinner fa-spin"></i> Création...',
      '<i class="fas fa-user-check mr-2"></i>Créer mon compte'
    );

    try {
      const response = await createUser(currentPhone, null);
      const autoUsername = response.data?.user?.username || '';
      successMsg.textContent = `Votre compte est prêt ! Votre nom d’utilisateur public : ${autoUsername} (modifiable plus tard)`;
      resetToStep('success');
      setTimeout(() => {
        closeModal();
        location.reload();
      }, 2500);
    } catch (err) {
      showError(err.response?.data?.message || 'Erreur');
    } finally {
      setButtonLoading(
        createBtn,
        false,
        '',
        '<i class="fas fa-user-check mr-2"></i>Créer mon compte'
      );
    }
  });

  retryBtn?.addEventListener('click', () => resetToStep('phone'));

  // ============================================================
  // 🔥 Error handling
  // ============================================================
  function showError(message) {
    if (errorMsg) errorMsg.textContent = message;
    resetToStep('error');
  }

  // ============================================================
  // 🔥 Password toggle (delegated, works for both store & admin)
  // ============================================================
  document.addEventListener('click', e => {
    const toggle = e.target.closest('.js-password-toggle');
    if (!toggle) return;

    // Find associated input within same form
    const form = toggle.closest('form');
    const input = form?.querySelector(
      'input[type="password"], input[type="text"]'
    );
    if (!input) return;

    const icon = toggle.querySelector('i');
    const isPassword = input.type === 'password';
    input.type = isPassword ? 'text' : 'password';
    icon?.classList.toggle('fa-eye', !isPassword);
    icon?.classList.toggle('fa-eye-slash', isPassword);
  });
})();
